if (!customElements.get('image-gallery-rail')) {
  class ImageGalleryRail extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-image-gallery-rail]');
      this.controls = this.querySelector('[data-image-gallery-controls]');
      this.previousButton = this.querySelector('[data-image-gallery-previous]');
      this.nextButton = this.querySelector('[data-image-gallery-next]');
      this.indicator = this.querySelector('[data-image-gallery-indicator]');
      this.indicatorValue = this.querySelector('[data-image-gallery-indicator-value]');

      if (!this.rail || !this.controls || !this.previousButton || !this.nextButton) return;

      this.abortController = new AbortController();
      this.reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileViewport = window.matchMedia('(max-width: 767px)');
      this.mobileStyle = this.dataset.mobileStyle || 'horizontal_scroll';
      this.carouselDescription = this.rail.dataset.carouselDescription || '';
      this.mobileInteraction = this.dataset.mobileInteraction || 'default';
      this.showMobileArrows = this.dataset.showMobileArrows === 'true';
      this.updateFrame = null;

      const listenerOptions = { signal: this.abortController.signal };
      this.previousButton.addEventListener('click', () => this.scrollByItem(-1), listenerOptions);
      this.nextButton.addEventListener('click', () => this.scrollByItem(1), listenerOptions);
      this.rail.addEventListener('click', (event) => this.handleItemClick(event), listenerOptions);
      this.rail.addEventListener('scroll', () => this.scheduleUpdate(), {
        passive: true,
        signal: this.abortController.signal,
      });
      this.handleViewportChange = () => {
        this.clearMobileHover();
        this.updateRailSemantics();
        this.scheduleMosaic();
        this.scheduleUpdate();
      };
      this.mobileViewport.addEventListener('change', this.handleViewportChange);

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
        this.resizeObserver.observe(this);
        this.resizeObserver.observe(this.rail);
      } else {
        window.addEventListener('resize', () => this.scheduleUpdate(), listenerOptions);
      }

      this.setupMosaic();
      this.updateRailSemantics();
      this.scheduleUpdate();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.mobileViewport?.removeEventListener('change', this.handleViewportChange);
      this.mosaicObserver?.disconnect();
      clearTimeout(this.mosaicDwellTimer);
      this.mosaicPhaseTimers?.forEach(clearTimeout);

      if (this.updateFrame !== null) {
        cancelAnimationFrame(this.updateFrame);
      }
    }

    scheduleUpdate() {
      if (this.updateFrame !== null) return;

      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = null;
        this.updateControls();
      });
    }

    updateRailSemantics() {
      if (this.mobileViewport.matches && this.mobileStyle !== 'horizontal_scroll') {
        this.rail.removeAttribute('aria-roledescription');
      } else {
        this.rail.setAttribute('aria-roledescription', this.carouselDescription);
      }
    }

    setupMosaic() {
      if (this.mobileStyle !== 'mosaic') return;

      this.mosaicQueue = this.getItems().slice(3);
      if (!this.mosaicQueue.length) return;

      this.mosaicPhaseTimers = [];
      this.mosaicVisible = false;
      this.mosaicLastSlot = -1;
      this.mosaicFocusHandler = () => this.scheduleMosaic();
      this.addEventListener('focusin', this.mosaicFocusHandler, { signal: this.abortController.signal });
      this.addEventListener('focusout', () => setTimeout(() => this.scheduleMosaic(), 0), { signal: this.abortController.signal });
      document.addEventListener('visibilitychange', this.mosaicFocusHandler, { signal: this.abortController.signal });
      this.reducedMotionMedia.addEventListener('change', this.mosaicFocusHandler, { signal: this.abortController.signal });
      this.addEventListener('shopify:block:select', (event) => this.revealMosaicBlock(event.target.closest('[data-image-gallery-item]')), { signal: this.abortController.signal });
      this.addEventListener('shopify:block:deselect', () => this.revealMosaicBlock(null), { signal: this.abortController.signal });

      if ('IntersectionObserver' in window) {
        this.mosaicObserver = new IntersectionObserver(([entry]) => {
          this.mosaicVisible = entry.isIntersecting;
          this.scheduleMosaic();
        }, { threshold: 0.1 });
        this.mosaicObserver.observe(this);
      } else {
        this.mosaicVisible = true;
        this.scheduleMosaic();
      }
    }

    canRotateMosaic() {
      return this.mobileStyle === 'mosaic'
        && this.isConnected
        && this.mosaicVisible
        && this.mobileViewport.matches
        && !this.reducedMotionMedia.matches
        && !(window.Shopify && window.Shopify.designMode)
        && !document.hidden
        && !this.contains(document.activeElement);
    }

    scheduleMosaic() {
      clearTimeout(this.mosaicDwellTimer);
      if (!this.mosaicQueue?.length || !this.canRotateMosaic()) return;
      const nextImage = this.mosaicQueue[0].querySelector('img');
      if (nextImage) {
        nextImage.loading = 'eager';
        if (typeof nextImage.decode === 'function') nextImage.decode().catch(() => {});
      }
      this.mosaicDwellTimer = setTimeout(() => this.rotateMosaic(), 3200);
    }

    rotateMosaic() {
      if (!this.canRotateMosaic()) return;
      const choices = this.mosaicQueue.length === 1
        ? [2]
        : [0, 1, 2].filter((slot) => slot !== this.mosaicLastSlot);
      const slot = choices[Math.floor(Math.random() * choices.length)];
      const outgoing = this.rail.querySelector(`[data-image-gallery-mosaic-slot="${slot + 1}"]`);
      const incoming = this.mosaicQueue.shift();
      if (!outgoing || !incoming) return;
      this.mosaicLastSlot = slot;
      outgoing.setAttribute('data-image-gallery-mosaic-fading', '');

      this.mosaicPhaseTimers.push(setTimeout(() => {
        if (this.contains(document.activeElement)) {
          outgoing.removeAttribute('data-image-gallery-mosaic-fading');
          this.mosaicQueue.unshift(incoming);
          this.mosaicPhaseTimers.length = 0;
          this.scheduleMosaic();
          return;
        }
        outgoing.removeAttribute('data-image-gallery-mosaic-slot');
        outgoing.removeAttribute('data-image-gallery-mosaic-fading');
        this.mosaicPhaseTimers.push(setTimeout(() => {
          incoming.setAttribute('data-image-gallery-mosaic-entering', '');
          incoming.setAttribute('data-image-gallery-mosaic-slot', String(slot + 1));
          void incoming.offsetWidth;
          incoming.setAttribute('data-image-gallery-mosaic-entering', 'visible');
          this.mosaicPhaseTimers.push(setTimeout(() => {
            incoming.removeAttribute('data-image-gallery-mosaic-entering');
            this.mosaicQueue.push(outgoing);
            this.mosaicPhaseTimers.length = 0;
            this.scheduleMosaic();
          }, 500));
        }, 220));
      }, 500));
    }

    revealMosaicBlock(item) {
      if (this.mosaicEditorSwap) {
        this.mosaicEditorSwap.item.removeAttribute('data-image-gallery-mosaic-slot');
        this.mosaicEditorSwap.displaced.setAttribute('data-image-gallery-mosaic-slot', '3');
        this.mosaicEditorSwap = null;
      }
      if (!item || item.hasAttribute('data-image-gallery-mosaic-slot')) return;
      const displaced = this.rail.querySelector('[data-image-gallery-mosaic-slot="3"]');
      if (!displaced) return;
      displaced.removeAttribute('data-image-gallery-mosaic-slot');
      item.setAttribute('data-image-gallery-mosaic-slot', '3');
      this.mosaicEditorSwap = { item, displaced };
    }

    handleItemClick(event) {
      if (!this.mobileViewport.matches || this.mobileInteraction !== 'hover') return;

      const item = event.target.closest('[data-image-gallery-hover-item]');
      if (!item || !this.rail.contains(item)) return;

      if (!item.classList.contains('is-hover-active')) {
        event.preventDefault();
        this.setMobileHover(item);
        return;
      }

      if (!item.matches('a[href]')) {
        item.classList.remove('is-hover-active');
      }
    }

    setMobileHover(activeItem) {
      for (const item of this.getItems()) {
        item.classList.toggle('is-hover-active', item === activeItem);
      }
    }

    clearMobileHover() {
      for (const item of this.getItems()) {
        item.classList.remove('is-hover-active');
      }
    }

    updateControls() {
      const items = this.getItems();
      const isMobile = this.mobileViewport.matches;
      const hasOverflow = (!isMobile || this.mobileStyle === 'horizontal_scroll')
        && this.rail.scrollWidth > this.rail.clientWidth + 1;
      const hasItems = items.length > 0;
      const showControls = hasOverflow && hasItems && (!isMobile || this.showMobileArrows);
      const showIndicator = hasOverflow && hasItems && isMobile;

      if (this.indicator) {
        this.indicator.hidden = !showIndicator;
      }

      if (showIndicator) {
        this.updateIndicator(items);
      }

      this.controls.hidden = !showControls;
      if (!showControls) {
        this.previousButton.disabled = true;
        this.nextButton.disabled = true;
        return;
      }

      const railRect = this.rail.getBoundingClientRect();
      const firstRect = items[0].getBoundingClientRect();
      const lastRect = items[items.length - 1].getBoundingClientRect();
      const isRtl = getComputedStyle(this.rail).direction === 'rtl';
      const tolerance = 2;

      this.previousButton.disabled = isRtl
        ? firstRect.right <= railRect.right + tolerance
        : firstRect.left >= railRect.left - tolerance;
      this.nextButton.disabled = isRtl
        ? lastRect.left >= railRect.left - tolerance
        : lastRect.right <= railRect.right + tolerance;
    }

    updateIndicator(items) {
      if (!this.indicatorValue || items.length === 0) return;

      const railRect = this.rail.getBoundingClientRect();
      const firstRect = items[0].getBoundingClientRect();
      const isRtl = getComputedStyle(this.rail).direction === 'rtl';
      const scrollableWidth = Math.max(this.rail.scrollWidth - this.rail.clientWidth, 0);
      const scrollOffset = isRtl
        ? Math.max(firstRect.right - railRect.right, 0)
        : Math.max(railRect.left - firstRect.left, 0);
      const visibleProgress = this.rail.scrollWidth > 0 ? this.rail.clientWidth / this.rail.scrollWidth : 1;
      const scrollProgress = scrollableWidth > 0 ? Math.min(scrollOffset / scrollableWidth, 1) : 1;
      const progress = Math.min(visibleProgress + scrollProgress * (1 - visibleProgress), 1);

      this.indicatorValue.style.setProperty('--horizontal-scroll-progress', progress.toFixed(4));
    }

    scrollByItem(direction) {
      const items = this.getItems();
      if (items.length === 0) return;

      const railRect = this.rail.getBoundingClientRect();
      const isRtl = getComputedStyle(this.rail).direction === 'rtl';
      const startEdge = isRtl ? railRect.right : railRect.left;
      let currentIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      for (const [index, item] of items.entries()) {
        const itemRect = item.getBoundingClientRect();
        const itemStartEdge = isRtl ? itemRect.right : itemRect.left;
        const distance = Math.abs(itemStartEdge - startEdge);

        if (distance < closestDistance) {
          currentIndex = index;
          closestDistance = distance;
        }
      }

      const targetIndex = Math.max(0, Math.min(items.length - 1, currentIndex + direction));
      const targetRect = items[targetIndex].getBoundingClientRect();
      const targetStartEdge = isRtl ? targetRect.right : targetRect.left;

      this.rail.scrollBy({
        left: targetStartEdge - startEdge,
        behavior: this.reducedMotionMedia.matches ? 'auto' : 'smooth',
      });
    }

    getItems() {
      return [...this.rail.querySelectorAll('[data-image-gallery-item]')];
    }
  }

  customElements.define('image-gallery-rail', ImageGalleryRail);
}
